/**
 * WP-06C-B-V — The resume-safe resolution sequence.
 *
 * The browser may re-enter a partially completed closure (a Loop already sitting
 * at `ready_to_verify` with its condition verified). This module holds the only
 * ordering rules, so they can be asserted without a browser:
 *
 *   * a condition the AUTHORITY already recorded as verified is never verified
 *     again — duplicate attestation is impossible on resume;
 *   * each step expects the version the authority reported for the step before
 *     it; no version is ever computed, incremented, or guessed here;
 *   * a following archive expects the version the RESOLVE returned.
 *
 * Nothing here talks to the network: the callers are injected.
 */

export type SequenceStepResult =
  | { readonly ok: true; readonly replayed: boolean; readonly recordVersion: number }
  | { readonly ok: false; readonly message: string; readonly retryable: boolean };

export interface ResolutionSequenceInput {
  /** The governed conditions exactly as the authoritative read reported them. */
  readonly conditions: readonly { readonly conditionId: string; readonly verified: boolean }[];
  /** The conditions the person attested to in this pass. */
  readonly attestedIds: readonly string[];
  /** The freshest authoritative version known to the caller. */
  readonly expectedVersion: number;
  readonly verifyCondition: (
    conditionId: string,
    expectedVersion: number,
  ) => Promise<SequenceStepResult>;
  readonly resolve: (expectedVersion: number) => Promise<SequenceStepResult>;
}

export type ResolutionSequenceOutcome =
  | {
      readonly ok: true;
      /** The version the authority reported for the resolve itself. */
      readonly recordVersion: number;
      readonly verifiedConditionIds: readonly string[];
    }
  | { readonly ok: false; readonly message: string; readonly retryable: boolean };

/** Conditions that still need attestation, in the caller's order, deduplicated. */
export function outstandingConditionIds(
  conditions: ResolutionSequenceInput["conditions"],
  attestedIds: readonly string[],
): readonly string[] {
  const verified = new Set(conditions.filter((c) => c.verified).map((c) => c.conditionId));
  const seen = new Set<string>();
  return attestedIds.filter((id) => {
    if (verified.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export async function runResolutionSequence(
  input: ResolutionSequenceInput,
): Promise<ResolutionSequenceOutcome> {
  const outstanding = outstandingConditionIds(input.conditions, input.attestedIds);
  let expected = input.expectedVersion;
  const verifiedConditionIds: string[] = [];

  for (const conditionId of outstanding) {
    const attested = await input.verifyCondition(conditionId, expected);
    if (!attested.ok) return attested;
    expected = attested.recordVersion;
    verifiedConditionIds.push(conditionId);
  }

  const resolved = await input.resolve(expected);
  if (!resolved.ok) return resolved;
  return { ok: true, recordVersion: resolved.recordVersion, verifiedConditionIds };
}
